import { useEffect, useRef, useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { ShoppingBag } from 'lucide-react';
import { useCart } from '@/lib/CartContext';
import { IDS } from '@/constants/testIds';

/**
 * CartNav — header cart button with:
 *  - Live count badge that "bumps" (pop animation) whenever the count changes
 *  - Hover-revealed mini-cart dropdown showing the first few line items,
 *    subtotal, and quick actions (View Cart / Continue Shopping)
 */
export default function CartNav() {
  const { items, count, subtotal } = useCart();
  const [bump, setBump] = useState(false);
  const [hover, setHover] = useState(false);
  const prevCount = useRef(count);
  const hoverTimer = useRef(null);

  // Trigger bump animation on any change to the count (add, remove, submit).
  useEffect(() => {
    if (prevCount.current !== count) {
      setBump(true);
      const t = setTimeout(() => setBump(false), 600);
      prevCount.current = count;
      return () => clearTimeout(t);
    }
  }, [count]);

  const openMini = () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    setHover(true);
  };
  const closeMini = () => {
    // Small delay so the pointer can travel from the nav link into the panel
    hoverTimer.current = setTimeout(() => setHover(false), 120);
  };

  const preview = items.slice(0, 4);
  const extra = Math.max(0, items.length - preview.length);

  return (
    <div className="relative" onMouseEnter={openMini} onMouseLeave={closeMini}>
      <NavLink
        to="/cart"
        data-testid={IDS.header.navCart}
        className={({ isActive }) =>
          `sans text-[0.72rem] tracking-[0.28em] uppercase flex items-center gap-2 transition-colors ${
            isActive ? 'text-white' : 'text-neutral-300 hover:text-white'
          }`
        }
      >
        <ShoppingBag size={16} strokeWidth={1.5} />
        Cart
        {count > 0 && (
          <span
            key={count /* re-mount to restart animation on every change */}
            data-testid="cart-count-badge"
            className={`sans text-[0.6rem] tracking-normal font-semibold rounded-full px-1.5 py-0.5 min-w-[1.25rem] text-center inline-block ${bump ? 'cart-badge-bump' : ''}`}
            style={{
              background: 'linear-gradient(135deg, #F2A6CE, #C2478A)',
              color: '#fff',
            }}
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </NavLink>

      {/* Mini-cart dropdown */}
      {hover && (
        <div
          className="mini-cart-panel absolute right-0 top-full mt-3 w-[22rem] z-50"
          data-testid="mini-cart-panel"
          role="dialog"
          aria-label="Mini cart"
        >
          {/* invisible hover bridge so the panel doesn't close between the nav and the panel */}
          <div className="absolute -top-3 left-0 right-0 h-3" />
          <div className="bg-white border border-neutral-200 shadow-[0_20px_40px_-15px_rgba(10,10,10,0.35)]">
            <div className="px-5 py-4 flex items-center justify-between border-b border-neutral-200">
              <p className="eyebrow">Your Cart</p>
              <p className="sans text-xs text-neutral-500 tracking-widest uppercase">{count} {count === 1 ? 'unit' : 'units'}</p>
            </div>

            {items.length === 0 ? (
              <div className="px-5 py-8 text-center">
                <p className="serif text-lg italic text-neutral-700">Your cart is empty.</p>
                <Link to="/products" className="btn-outline mt-4 inline-flex" style={{ padding: '0.55rem 1rem', fontSize: '0.65rem' }}>
                  Browse Catalog
                </Link>
              </div>
            ) : (
              <>
                <ul className="max-h-72 overflow-y-auto divide-y divide-neutral-100" data-testid="mini-cart-items">
                  {preview.map((it) => (
                    <li key={it.product_id} className="px-5 py-3 flex items-center gap-3">
                      <div className="w-12 h-12 flex-shrink-0 bg-neutral-100 border border-neutral-200 overflow-hidden">
                        {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="sans text-xs uppercase tracking-widest text-neutral-500 truncate">{it.brand}</p>
                        <p className="serif text-sm text-neutral-900 leading-tight truncate">{it.name}</p>
                        <p className="sans text-xs text-neutral-500 mt-0.5">
                          {it.qty} × ${Number(it.your_unit_price || 0).toFixed(2)}
                        </p>
                      </div>
                      <p className="serif text-sm text-neutral-900 flex-shrink-0">${Number(it.line_total || 0).toFixed(2)}</p>
                    </li>
                  ))}
                </ul>
                {extra > 0 && (
                  <p className="px-5 py-2 sans text-xs text-neutral-500 tracking-widest uppercase text-center border-t border-neutral-100">
                    + {extra} more item{extra === 1 ? '' : 's'}
                  </p>
                )}
                <div className="px-5 py-4 border-t border-neutral-200 flex items-baseline justify-between">
                  <span className="sans text-xs uppercase tracking-widest text-neutral-500">Subtotal</span>
                  <span className="serif text-xl text-neutral-900" data-testid="mini-cart-subtotal">${Number(subtotal || 0).toFixed(2)}</span>
                </div>
                <div className="px-5 pb-5 grid grid-cols-2 gap-2">
                  <Link to="/products" className="btn-outline" style={{ padding: '0.6rem 0.5rem', fontSize: '0.62rem' }} data-testid="mini-cart-continue">
                    Continue
                  </Link>
                  <Link to="/cart" className="btn-primary" style={{ padding: '0.6rem 0.5rem', fontSize: '0.62rem' }} data-testid="mini-cart-view">
                    View Cart
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
